import {
    useCallback,
    useRef,
    type FC,
    type ReactElement,
    type ChangeEvent,
    type FocusEvent,
    type KeyboardEvent,
    type MouseEvent,
    type FormEvent,
} from 'react';
import { defineMessages, useIntl } from 'dibs-react-intl';
import classnames from 'classnames';

import Close from 'dibs-icons/exports/Close';
import ClearIcon from 'dibs-icons/exports/legacy/Close';
import SearchIcon from 'dibs-icons/exports/legacy/MagnifyingGlass';
import Search from 'dibs-icons/exports/Search';

// components
import { SIZES } from '../Common/constants';
import { StyledButton } from '../Common/Button/StyledButton';
import { Input } from '../Input';
import { RotatingArrow } from '../RotatingArrow';
import { DropdownContent } from './DropdownContent';

// styles
import dibsCss from 'dibs-css';
import styles from './main.scss';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { small, medium, large } = SIZES;

const labels = defineMessages({
    search: { id: 'dibsElements.search', defaultMessage: 'Search' },
    expandCollapseDropdown: {
        id: 'dibsElements.SearchBar.expandOrCollapseDropdown',
        defaultMessage: 'Expand or collapse dropdown',
    },
    clear: { id: 'dibsElements.SearchBar.clear', defaultMessage: 'Clear search terms' },
});

export type PropsType = {
    dataTn: string;
    value?: string;
    defaultValue?: string;
    onChange?: (value: string) => void;
    size?: typeof small | typeof medium | typeof large;
    autoComplete?: boolean | string;
    disabled?: boolean;
    focusOnClear?: boolean;
    hasRightSearchButton?: boolean;
    isClearButtonHidden?: boolean;
    noBorder?: boolean;
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
    onClear?: (e: MouseEvent<HTMLElement>) => void;
    onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
    placeholder?: string;
    toggleDropdown?: () => void;
    dropdownIsOpen?: boolean;
    dropdownContent?: ReactElement;
    isHeaderSearch?: boolean;
    leftMargin?: typeof small;
    autoFocus?: boolean;
    roundedCorners?: boolean;
};

const noop = (): void => {};

export const SearchBar: FC<PropsType> = props => {
    const {
        autoComplete = false,
        disabled = false,
        focusOnClear = false,
        hasRightSearchButton = true,
        noBorder = false,
        dropdownIsOpen = false,
        onBlur = noop,
        onClear = noop,
        onFocus = noop,
        onKeyDown = noop,
        dataTn,
        isClearButtonHidden,
        onChange,
        onSubmit,
        toggleDropdown,
        placeholder,
        size,
        value,
        defaultValue,
        dropdownContent,
        isHeaderSearch,
        leftMargin,
        autoFocus,
        roundedCorners,
    } = props;
    const intl = useIntl();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const doChange = useCallback(
        (val: string) => {
            if (onChange) {
                onChange(val);
            }
        },
        [onChange]
    );

    const onInputChangeHandler = (e: ChangeEvent<HTMLInputElement>): void => {
        doChange(e.target.value);
    };

    const handleOnBlur = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            inputRef.current = e.target;
            if (onBlur) {
                onBlur(e);
            }
        },
        [onBlur, inputRef]
    );

    const handleOnClear = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            if (
                inputRef &&
                inputRef.current &&
                typeof inputRef.current.focus === 'function' &&
                focusOnClear
            ) {
                inputRef.current.focus();
            }
            doChange('');
            onClear(e);
        },
        [doChange, onClear, inputRef, focusOnClear]
    );

    const hideClearButton =
        typeof isClearButtonHidden !== 'undefined' ? isClearButtonHidden : !value;

    const showRightSearchButton = hasRightSearchButton && !toggleDropdown;
    // TODO use isHeaderSearch instead of roundedCorners if search_bar_redesign abtest is successful,
    // cause primary search button should always be visible for desktop users (showRightSearchButton = true)
    const showPrimarySearchButton = showRightSearchButton && (!hideClearButton || roundedCorners);

    // default to medium size
    let buttonSize = styles.mediumButton;
    let searchButtonSize = styles.mediumSearch;
    if (size === small) {
        buttonSize = styles.smallButton;
        searchButtonSize = styles.smallSearch;
    } else if (size === large) {
        buttonSize = styles.largeButton;
        searchButtonSize = styles.largeSearch;
    }

    const searchButton = (
        <div tabIndex={-1} data-tn={`${dataTn}-search-button-wrapper`}>
            <StyledButton
                ariaLabel={intl.formatMessage(labels.search)}
                type="submit"
                dataTn={`${dataTn}-search-button`}
                className={classnames({
                    [styles.defaultSearch]: !size,
                    [searchButtonSize]: !!size,
                    [styles.noClick]: hideClearButton,
                    [styles.fullyTransparent]:
                        (!roundedCorners && hideClearButton) || !showRightSearchButton,
                    [dibsCss.fillWhite]: showPrimarySearchButton,
                    // TODO remove this css class if search_bar_redesign abtest is successful
                    [styles.headerSearchButton]: isHeaderSearch && !roundedCorners,
                    [styles.roundedButton]: roundedCorners && showRightSearchButton,
                })}
                disabled={disabled}
                isPrimary={showPrimarySearchButton}
                roundedCorners={roundedCorners}
            >
                {/* TODO use isHeaderSearch instead of roundedCorners after search_bar_redesign
                abtest is complete */}
                {roundedCorners ? <Search variation="24" /> : <SearchIcon />}
            </StyledButton>
        </div>
    );

    const clearButton = (
        <div tabIndex={-1} data-tn={`${dataTn}-clear-wrapper`}>
            <StyledButton
                ariaLabel={intl.formatMessage(labels.clear)}
                type="button"
                dataTn={`${dataTn}-clear`}
                className={classnames(styles.fullyTransparent, {
                    [styles.defaultButton]: !size,
                    [buttonSize]: !!size,
                    [styles.hideButton]: hideClearButton,
                    [styles.showButton]: !hideClearButton,
                    // TODO remove this css class if search_bar_redesign abtest is successful
                    [styles.headerSearchClearButton]: isHeaderSearch && !roundedCorners,
                    [styles.clearButtonMargin]: !showRightSearchButton && roundedCorners && !value,
                    [styles.clearButtonMarginRemoved]:
                        !showRightSearchButton && roundedCorners && value,
                })}
                disabled={disabled}
                isPrimary={false}
                onClick={handleOnClear}
                tabIndex={hideClearButton ? -1 : undefined}
            >
                {/* TODO use isHeaderSearch instead of roundedCorners after search_bar_redesign
                abtest is complete */}
                {roundedCorners ? <Close variation="20" /> : <ClearIcon />}
            </StyledButton>
        </div>
    );

    const rightSearchVariation = (
        <Input
            // base input fields
            autoComplete={autoComplete}
            autoCorrect={false}
            dataTn={dataTn}
            disabled={disabled}
            noBorder={noBorder}
            borderRounded={roundedCorners}
            onBlur={handleOnBlur}
            onChange={onInputChangeHandler}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            size={size}
            spellCheck={false}
            value={value}
            defaultValue={defaultValue}
            title={intl.formatMessage(labels.search)}
            ariaLabel={intl.formatMessage(labels.search)}
            leftMargin={leftMargin}
            isHeaderInput={isHeaderSearch}
            // right variation fields
            rightDecorator={
                <>
                    {clearButton}
                    {searchButton}
                </>
            }
            rightDecoratorClass={classnames(styles.decoratorWrapper, {
                [styles.shiftRight]: !noBorder,
            })}
            autoFocus={autoFocus}
        />
    );

    const leftSearchVariation = (
        <Input
            // base input fields
            autoComplete={autoComplete}
            autoCorrect={false}
            dataTn={dataTn}
            disabled={disabled}
            noBorder={noBorder}
            borderRounded={roundedCorners}
            onBlur={handleOnBlur}
            onChange={onInputChangeHandler}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            size={size}
            spellCheck={false}
            value={value}
            defaultValue={defaultValue}
            title={intl.formatMessage(labels.search)}
            ariaLabel={intl.formatMessage(labels.search)}
            isHeaderInput={isHeaderSearch}
            // left variation fields
            leftDecorator={searchButton}
            leftDecoratorClass={styles.decoratorWrapper}
            leftMargin={leftMargin}
            rightDecorator={
                toggleDropdown ? (
                    <>
                        {clearButton}
                        <StyledButton
                            ariaLabel={intl.formatMessage(labels.expandCollapseDropdown)}
                            type="button"
                            className={classnames(styles.fullyTransparent, buttonSize)}
                            dataTn={`${dataTn}-dropdown-button`}
                            disabled={disabled}
                            isPrimary={false}
                            onClick={(event: MouseEvent<HTMLElement>) => {
                                // stop propagation so that useOutsideClick does not also fire
                                event.nativeEvent.stopImmediatePropagation();
                                toggleDropdown();
                            }}
                        >
                            <RotatingArrow direction={dropdownIsOpen ? 'up' : 'down'} />
                        </StyledButton>
                    </>
                ) : (
                    clearButton
                )
            }
            rightDecoratorClass={styles.decoratorWrapper}
            autoFocus={autoFocus}
        />
    );

    if (onSubmit) {
        return (
            <>
                <form data-tn={`${dataTn}-form`} onSubmit={onSubmit}>
                    <div className={styles.wrapper}>
                        {showRightSearchButton ? rightSearchVariation : leftSearchVariation}
                    </div>
                </form>
                {toggleDropdown && dropdownContent && (
                    <DropdownContent
                        dropdownContent={dropdownContent}
                        dropdownIsOpen={dropdownIsOpen}
                        toggleDropdown={toggleDropdown}
                    />
                )}
            </>
        );
    }

    const wrapperClassName = classnames(styles.wrapper, {
        // TODO remove this css class if search_bar_redesign abtest is successful
        [styles.openDropdown]: dropdownIsOpen && isHeaderSearch && !roundedCorners,
        // TODO use isHeaderSearch instead of roundedCorners after search_bar_redesign abtest is complete
        [dibsCss.wFull]: roundedCorners,
    });

    return (
        <>
            <div className={wrapperClassName}>
                {showRightSearchButton ? rightSearchVariation : leftSearchVariation}
            </div>
            {toggleDropdown && dropdownContent && (
                <DropdownContent
                    dropdownContent={dropdownContent}
                    dropdownIsOpen={dropdownIsOpen}
                    toggleDropdown={toggleDropdown}
                />
            )}
        </>
    );
};
